import { KIND_ORDER, sortDefinitions } from "src/utils/definition-order";
import { isMapDefinition, mapPageHref, showDefinitionInMenu } from "src/components/map/map-links";

/**
 * What a tile on /produkti shows and where it leads.
 *
 * One flat, ordered list rather than "a section per kind": the page used to
 * draw a heading and a rule across the whole width for each kind, and then
 * one card under it. With two definitions live that was two headings, two
 * cards and an empty page below them — and nothing at all about photo
 * prints, calendars, bundles or gift cards, which the header's own Produkti
 * menu lists. The tiles here are that menu, in the order a customer is
 * likely to want them: the everyday product first, the big ones next, the
 * occasion products after, and the ways to pay or gift last.
 */
export interface ProduktiDefinition {
  id: string;
  name?: string | Record<string, string>;
  price?: number;
  priceCurrencies?: Record<string, number | null>;
  previewImageKey?: string;
  previewImageUrl?: string | null;
  kind?: string;
  active?: boolean;
}

export type ProduktiHero =
  /** A lifestyle photograph, cropped to fill the frame. */
  | { kind: "image"; src: string }
  /** The definition's own preview, shown whole on a mat. */
  | { kind: "definition"; def: ProduktiDefinition }
  /** No photograph exists for this one; a themed icon stands in. */
  | { kind: "icon"; icon: "bundle" | "gift" }
  /** Definitions still loading: keeps the grid's shape while they arrive. */
  | { kind: "skeleton" };

export interface ProduktiTile {
  key: string;
  title: string;
  description: string;
  href: string;
  /** Already formatted, e.g. "že od 1,50 €". Absent when no figure is confirmed. */
  priceLabel?: string;
  hero: ProduktiHero;
  /**
   * The verb on the tile. A printed thing is made ("create"); a bundle or a
   * gift card is chosen from a list, so "view" — "create a bundle" would
   * promise an editor that is not there.
   */
  cta: "create" | "view";
}

export interface ProduktiFlags {
  artCatalogueEnabled: boolean;
  mapsEnabled: boolean;
  bundlesOffered: boolean;
  giftCardsEnabled: boolean;
}

export interface BuildProduktiTilesInput {
  locale: string;
  definitions: ProduktiDefinition[];
  definitionsLoading: boolean;
  flags: ProduktiFlags;
  /** Full-key translation, "produkti.photos" style. */
  translate: (key: string) => string;
  /** A definition's name in the visitor's language. */
  localizedName: (def: ProduktiDefinition) => string;
  /** "from X" for a definition, or undefined when it has no price of its own. */
  definitionPriceLabel: (def: ProduktiDefinition) => string | undefined;
  /** Confirmed entry prices for the static tiles, already formatted. */
  staticPriceLabel: (key: "calendar" | "largePaper" | "largeCanvas") => string | undefined;
  /** Optional template-category deep link to carry onto editor links. */
  filterParam?: string | null;
}

export { KIND_ORDER, sortDefinitions };

/**
 * "From" prices on the two large-print tiles, per market, read off the live
 * variants (2026-10-25): paper is the 40 × 60 the paper door opens on
 * (PAPER-40x60), canvas the cheapest stretched size (CANVAS-30x40). A market
 * without a confirmed figure shows none.
 */
export const LARGE_PRINT_PRICES: Record<string, { largePaper: string; largeCanvas: string }> = {
  sl: { largePaper: "19,90 €", largeCanvas: "14,90 €" },
  hr: { largePaper: "19,90 €", largeCanvas: "14,90 €" },
  sk: { largePaper: "19,90 €", largeCanvas: "14,90 €" },
  pl: { largePaper: "85 zł", largeCanvas: "63 zł" },
};

/** Kinds whose editor is driven by templates — their tile can show one. */
export const TEMPLATE_KINDS_WITH_PREVIEW = new Set(["greeting-card", "invitation", "menu"]);

const KIND_DESCRIPTION_KEY: Record<string, string> = {
  canvas: "produkti.wallArtDescription",
  "greeting-card": "produkti.kindDescriptions.greeting-card",
  invitation: "produkti.kindDescriptions.invitation",
  menu: "produkti.kindDescriptions.menu",
  map: "mapPrint.lead",
};

/**
 * Where a definition tile leads.
 *
 * Canvas definitions go to the wall-art hub while the catalogue is open: the
 * hub offers the same canvas from an upload, from the art collection or from
 * photography, and linking past it hides two of the three. With the
 * catalogue closed the canvas still sells from an upload, so its own editor
 * is the door — never /design, which is the greeting-card one.
 */
export function definitionHref(
  def: ProduktiDefinition,
  locale: string,
  flags: Pick<ProduktiFlags, "artCatalogueEnabled">,
  filterParam?: string | null,
): string {
  if (isMapDefinition(def)) return mapPageHref(locale);
  if (def.kind === "canvas") {
    return flags.artCatalogueEnabled ? `/${locale}/wall-art` : `/${locale}/canvas/${def.id}`;
  }
  return `/${locale}/design/${def.id}${filterParam ? `?filter=${filterParam}` : ""}`;
}

export function buildProduktiTiles(input: BuildProduktiTilesInput): ProduktiTile[] {
  const {
    locale,
    definitions,
    definitionsLoading,
    flags,
    translate,
    localizedName,
    definitionPriceLabel,
    staticPriceLabel,
    filterParam,
  } = input;

  const tiles: ProduktiTile[] = [
    {
      key: "photos",
      title: translate("produkti.photos"),
      description: translate("produkti.photosDescription"),
      href: `/${locale}`,
      hero: { kind: "image", src: "/images/landing-page/dojencek.jpg" },
      cta: "create",
    },
    {
      key: "calendars",
      title: translate("produkti.calendars"),
      description: translate("produkti.calendarsDescription"),
      href: `/${locale}/calendar`,
      priceLabel: staticPriceLabel("calendar"),
      hero: { kind: "image", src: "/images/next-steps/calendar.webp" },
      cta: "create",
    },
  ];

  // Large prints are two products to the customer, not one "Povečave" shelf:
  // photo paper and canvas differ in price, look and how they arrive, and
  // canvas is where we beat the market. Both open the same editor on their
  // own material (?material=, see finishForMaterialParam).
  tiles.push(
    {
      key: "large-paper",
      title: translate("produkti.largePaper"),
      description: translate("produkti.largePaperDescription"),
      href: `/${locale}/canvas?material=paper`,
      priceLabel: staticPriceLabel("largePaper"),
      hero: { kind: "image", src: "/images/next-steps/large-paper.webp" },
      cta: "create",
    },
    {
      key: "large-canvas",
      title: translate("produkti.largeCanvas"),
      description: translate("produkti.largeCanvasDescription"),
      href: `/${locale}/canvas?material=canvas`,
      priceLabel: staticPriceLabel("largeCanvas"),
      hero: { kind: "image", src: "/images/next-steps/canvas.webp" },
      cta: "create",
    },
  );

  // The art shop: ready-made prints of famous works, next to the two doors
  // for the customer's own photo. Behind the same switch as the header link.
  // No "from" price: every work has its own sizes, so one figure would mislead.
  if (flags.artCatalogueEnabled) {
    tiles.push({
      key: "art",
      title: translate("produkti.art"),
      description: translate("produkti.artDescription"),
      href: `/${locale}/art`,
      hero: { kind: "image", src: "/images/next-steps/art.webp" },
      cta: "view",
    });
  }

  if (definitionsLoading) {
    tiles.push(
      { key: "skeleton-1", title: "", description: "", href: "", hero: { kind: "skeleton" }, cta: "create" },
      { key: "skeleton-2", title: "", description: "", href: "", hero: { kind: "skeleton" }, cta: "create" },
    );
  } else {
    const visible = sortDefinitions(
      definitions.filter(
        (d) =>
          d.active !== false &&
          showDefinitionInMenu(d, { mapsEnabled: flags.mapsEnabled }) &&
          // The two large-print tiles above already are the canvas
          // definition; listing it too would show one product three times.
          d.kind !== "canvas",
      ),
    );
    for (const def of visible) {
      const kind = def.kind ?? "other";
      const descriptionKey = KIND_DESCRIPTION_KEY[kind];
      tiles.push({
        key: def.id,
        title: localizedName(def),
        description: descriptionKey ? translate(descriptionKey) : "",
        href: definitionHref(def, locale, flags, filterParam),
        priceLabel: definitionPriceLabel(def),
        // A map has no template and no uploaded preview: a map print on a
        // living-room wall (the editor's "Na steni" view) stands in.
        hero: isMapDefinition(def)
          ? { kind: "image", src: "/images/next-steps/map-mesta.webp" }
          : { kind: "definition", def },
        cta: "create",
      });
    }
  }

  if (flags.bundlesOffered) {
    tiles.push({
      key: "bundles",
      title: translate("bundles.pageTitle"),
      description: translate("bundles.pageSubtitle"),
      href: `/${locale}/paketi`,
      hero: { kind: "icon", icon: "bundle" },
      cta: "view",
    });
  }

  if (flags.giftCardsEnabled) {
    tiles.push({
      key: "gift-card",
      title: translate("giftCard.title"),
      description: translate("giftCard.lead"),
      href: `/${locale}/darilni-bon`,
      hero: { kind: "icon", icon: "gift" },
      cta: "view",
    });
  }

  return tiles;
}
